import React, { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { api } from "../../../services/api";
import { CreateListingDto } from "../../../services/api/LfgApi";
import { ApiError } from "../../../services/api/BaseApi";
import styles from "./CreateListingForm.module.scss";

interface CreateListingFormProps {
  games?: Array<{ gameId: string; name: string }>;
  onSuccess?: () => void;
}

interface FormData {
  gameId: string;
  title: string;
  maxPlayers: number;
  description: string;
  gameMode: string;
  region: string;
  skillLevel: string;
  voiceRequired: boolean;
}

interface CreationError {
  message: string;
  isCreationFailure: boolean;
}

const CreateListingForm: React.FC<CreateListingFormProps> = ({ games, onSuccess }) => {
  const [serverError, setServerError] = useState<CreationError | null>(null);
  const lastSubmittedData = useRef<FormData | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      gameId: "",
      title: "",
      maxPlayers: 5,
      description: "",
      gameMode: "",
      region: "",
      skillLevel: "",
      voiceRequired: false,
    },
  });

  const onSubmit = async (data: FormData) => {
    setServerError(null);
    lastSubmittedData.current = data;
    try {
      const dto: CreateListingDto = {
        gameId: data.gameId,
        title: data.title,
        maxPlayers: data.maxPlayers,
        description: data.description || undefined,
        preferences: {
          gameMode: data.gameMode || undefined,
          region: data.region || undefined,
          skillLevel: (data.skillLevel as "casual" | "competitive") || undefined,
          voiceRequired: data.voiceRequired || false,
        },
      };
      await api.lfg.createListing(dto);
      reset();
      onSuccess?.();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setServerError({ message: "You already have an active listing for this game.", isCreationFailure: false });
        } else if (err.status === 400 && err.data?.errors) {
          const fieldErrors = err.data.errors as Record<string, string>;
          Object.entries(fieldErrors).forEach(([field, message]) => {
            setError(field as keyof FormData, { type: "server", message });
          });
        } else if (err.status === 500 && err.data?.code === "LOBBY_CREATION_FAILED") {
          setServerError({ message: err.data?.message || "Failed to create lobby. Please try again.", isCreationFailure: true });
        } else {
          setServerError({ message: err.message || "Something went wrong. Please try again.", isCreationFailure: false });
        }
      } else {
        setServerError({ message: "Something went wrong. Please try again.", isCreationFailure: false });
      }
    }
  };

  const handleRetry = () => {
    const currentData = getValues();
    onSubmit(currentData);
  };

  return (
    <form className={styles.createListingForm} onSubmit={handleSubmit(onSubmit)} noValidate>
      {serverError && (
        <div
          className={`${styles.serverError} ${serverError.isCreationFailure ? styles.creationError : ""}`}
          role="alert"
        >
          <span className={styles.errorText}>{serverError.message}</span>
          {serverError.isCreationFailure && (
            <button
              type="button"
              className={styles.retryButton}
              onClick={handleRetry}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Retrying..." : "Try Again"}
            </button>
          )}
        </div>
      )}

      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="gameId">
          Game <span aria-hidden="true">*</span>
        </label>
        <select
          id="gameId"
          className={`${styles.select} ${errors.gameId ? styles.inputError : ""}`}
          aria-required="true"
          aria-invalid={!!errors.gameId}
          {...register("gameId", { required: "Please select a game" })}
        >
          <option value="">Select a game</option>
          {games?.map((game) => (
            <option key={game.gameId} value={game.gameId}>
              {game.name}
            </option>
          ))}
        </select>
        {errors.gameId && <span className={styles.errorMessage} role="alert">{errors.gameId.message}</span>}
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="title">
          Title <span aria-hidden="true">*</span>
        </label>
        <input
          id="title"
          type="text"
          className={`${styles.input} ${errors.title ? styles.inputError : ""}`}
          placeholder="Looking for teammates..."
          aria-required="true"
          aria-invalid={!!errors.title}
          {...register("title", {
            required: "Title is required",
            maxLength: { value: 100, message: "Title must be 100 characters or less" },
          })}
        />
        {errors.title && <span className={styles.errorMessage} role="alert">{errors.title.message}</span>}
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="maxPlayers">
          Max Players <span aria-hidden="true">*</span>
        </label>
        <input
          id="maxPlayers"
          type="number"
          className={`${styles.input} ${errors.maxPlayers ? styles.inputError : ""}`}
          aria-required="true"
          aria-invalid={!!errors.maxPlayers}
          {...register("maxPlayers", {
            required: "Max players is required",
            valueAsNumber: true,
            min: { value: 2, message: "Minimum 2 players" },
            max: { value: 20, message: "Maximum 20 players" },
          })}
        />
        {errors.maxPlayers && <span className={styles.errorMessage} role="alert">{errors.maxPlayers.message}</span>}
      </div>

      <div className={styles.fieldGroup}>
        <label className={styles.label} htmlFor="description">
          Description
        </label>
        <textarea
          id="description"
          className={styles.textarea}
          placeholder="Tell others what you're looking for..."
          rows={3}
          {...register("description")}
        />
      </div>

      <div className={styles.preferencesSection}>
        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="gameMode">
            Game Mode
          </label>
          <select id="gameMode" className={styles.select} {...register("gameMode")}>
            <option value="">Any</option>
            <option value="Ranked">Ranked</option>
            <option value="Casual">Casual</option>
            <option value="Custom">Custom</option>
          </select>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="region">
            Region
          </label>
          <select id="region" className={styles.select} {...register("region")}>
            <option value="">Any</option>
            <option value="NA">NA</option>
            <option value="EU">EU</option>
            <option value="APAC">APAC</option>
            <option value="SA">SA</option>
          </select>
        </div>

        <div className={styles.fieldGroup}>
          <label className={styles.label} htmlFor="skillLevel">
            Skill Level
          </label>
          <select id="skillLevel" className={styles.select} {...register("skillLevel")}>
            <option value="">Any</option>
            <option value="casual">Casual</option>
            <option value="competitive">Competitive</option>
          </select>
        </div>

        <div className={styles.fieldGroup}>
          <div className={styles.toggleGroup}>
            <input
              id="voiceRequired"
              type="checkbox"
              className={styles.checkbox}
              {...register("voiceRequired")}
            />
            <label className={styles.toggleLabel} htmlFor="voiceRequired">
              Voice Required
            </label>
          </div>
        </div>
      </div>

      <button type="submit" className={styles.submitButton} disabled={isSubmitting}>
        {isSubmitting ? "Creating..." : "Create Listing"}
      </button>
    </form>
  );
};

export default CreateListingForm;
